import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { payrollService, employeeService } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
    Banknote,
    Calculator,
    CheckCircle,
    Download,
    FileText,
    History,
    Loader2,
    Settings2,
} from 'lucide-react';
import { format } from 'date-fns';

const MONTHS = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' },
];

const YEARS = [2024, 2025, 2026];

export default function PayrollPage() {
    const { user, isAdmin } = useAuth();
    const [loading, setLoading] = useState(true);
    const [employees, setEmployees] = useState([]);
    const [myPayslips, setMyPayslips] = useState([]);

    // Admin/HR states
    const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [payrollLoading, setPayrollLoading] = useState(false);

    // Payroll runs history
    const [payrollRuns, setPayrollRuns] = useState([]);
    const [finalizeLoading, setFinalizeLoading] = useState(null);

    // EPF report state
    const [epfMonth, setEpfMonth] = useState(new Date().getMonth() + 1);
    const [epfYear, setEpfYear] = useState(new Date().getFullYear());

    // Salary Structure states
    const [showSalaryDialog, setShowSalaryDialog] = useState(false);
    const [editingEmployee, setEditingEmployee] = useState(null);
    const [salaryForm, setSalaryForm] = useState({
        // Earnings
        basic_salary: 0,
        hra: 0,
        conveyance_allowance: 1600,
        medical_allowance: 1250,
        special_allowance: 0,
        lta: 0,
        other_allowances: 0,
        // Deductions
        epf_applicable: true,
        esi_applicable: false,
        professional_tax: 200,
        mediclaim: 0,
        tds: 0,
        other_deductions: 0,
        other_deductions_reason: '',
        effective_from: format(new Date(), 'yyyy-MM-dd'),
    });

    const canManagePayroll = isAdmin();

    useEffect(() => {
        const loadData = async () => {
            setLoading(true);
            try {
                if (canManagePayroll) {
                    const [emps, runs] = await Promise.all([
                        employeeService.list(),
                        payrollService.getRuns(),
                    ]);
                    setEmployees(emps);
                    setPayrollRuns(runs);
                } else {
                    const slips = await payrollService.getMyPayslips();
                    setMyPayslips(slips);
                }
            } catch (error) {
                console.error('Failed to load payroll data:', error);
                toast.error('Failed to load data');
            } finally {
                setLoading(false);
            }
        };

        loadData();
    }, [canManagePayroll]);

    const handleOpenSalary = async (emp) => {
        setEditingEmployee(emp);
        try {
            const struct = await payrollService.getSalaryStructure(emp.id);
            if (struct) {
                setSalaryForm({
                    basic_salary: struct.basic_salary || 0,
                    hra: struct.hra || 0,
                    conveyance_allowance: struct.conveyance_allowance ?? struct.conveyance ?? 1600,
                    medical_allowance: struct.medical_allowance ?? 1250,
                    special_allowance: struct.special_allowance || 0,
                    lta: struct.lta || 0,
                    other_allowances: struct.other_allowances || 0,
                    epf_applicable: struct.epf_applicable ?? true,
                    esi_applicable: struct.esi_applicable ?? false,
                    professional_tax: struct.professional_tax ?? 200,
                    mediclaim: struct.mediclaim || 0,
                    tds: struct.tds || 0,
                    other_deductions: struct.other_deductions || 0,
                    other_deductions_reason: struct.other_deductions_reason || '',
                    effective_from: struct.effective_from || format(new Date(), 'yyyy-MM-dd'),
                });
            } else {
                setSalaryForm({
                    basic_salary: 0,
                    hra: 0,
                    conveyance_allowance: 1600,
                    medical_allowance: 1250,
                    special_allowance: 0,
                    lta: 0,
                    other_allowances: 0,
                    epf_applicable: true,
                    esi_applicable: false,
                    professional_tax: 200,
                    mediclaim: 0,
                    tds: 0,
                    other_deductions: 0,
                    other_deductions_reason: '',
                    effective_from: format(new Date(), 'yyyy-MM-dd'),
                });
            }
            setShowSalaryDialog(true);
        } catch (error) {
            toast.error('Failed to load salary structure');
        }
    };

    const handleSaveSalary = async () => {
        try {
            await payrollService.createSalaryStructure({
                employee_id: editingEmployee.id,
                ...salaryForm
            });
            toast.success('Salary structure updated');
            setShowSalaryDialog(false);
        } catch (error) {
            toast.error('Failed to update salary');
        }
    };

    const handleRunPayroll = async () => {
        setPayrollLoading(true);
        try {
            const result = await payrollService.runPayroll({ month: selectedMonth, year: selectedYear });
            toast.success(`Payroll generated: ${result.payslips_generated} payslips created`);
            const runs = await payrollService.getRuns();
            setPayrollRuns(runs);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to run payroll');
        } finally {
            setPayrollLoading(false);
        }
    };

    const handleFinalizePayroll = async (runId) => {
        setFinalizeLoading(runId);
        try {
            const result = await payrollService.finalizePayroll(runId);
            toast.success(result.message || 'Payroll finalized');
            const runs = await payrollService.getRuns();
            setPayrollRuns(runs);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to finalize payroll');
        } finally {
            setFinalizeLoading(null);
        }
    };

    const handleDownloadEpf = async () => {
        try {
            const data = await payrollService.getEpfReport(epfMonth, epfYear);
            const rows = [['Employee Code', 'Employee Name', 'UAN', 'Gross', 'EPF Employee', 'EPF Employer']];
            data.forEach(r => rows.push([r.employee_code, r.employee_name, r.uan || '', r.gross_salary, r.epf_employee, r.epf_employer]));
            const csv = rows.map(r => r.join(',')).join('\n');
            const blob = new Blob([csv], { type: 'text/csv' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `EPF_Report_${epfMonth}_${epfYear}.csv`);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            toast.success('EPF report downloaded');
        } catch (error) {
            toast.error('Failed to download EPF report');
        }
    };

    const handleDownloadPayslip = async (payslipId, month, year) => {
        try {
            const blob = await payrollService.downloadPayslip(payslipId);
            const url = window.URL.createObjectURL(new Blob([blob]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `Payslip_${month}_${year}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
        } catch (error) {
            toast.error('Failed to download payslip');
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="spinner" />
            </div>
        );
    }

    return (
        <div className="space-y-6 fade-in">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold tracking-tight">Payroll</h1>
                    <p className="text-muted-foreground mt-1">
                        {canManagePayroll
                            ? 'Manage employee salary structures and process monthly payroll'
                            : 'View and download your monthly payslips'}
                    </p>
                </div>
            </div>

            <Tabs defaultValue={canManagePayroll ? "management" : "my-payslips"} className="w-full">
                <TabsList>
                    {canManagePayroll && (
                        <TabsTrigger value="management">
                            <Banknote className="w-4 h-4 mr-2" />
                            Payroll Management
                        </TabsTrigger>
                    )}
                    <TabsTrigger value="my-payslips">
                        <FileText className="w-4 h-4 mr-2" />
                        My Payslips
                    </TabsTrigger>
                    {canManagePayroll && (
                        <TabsTrigger value="settings">
                            <Settings2 className="w-4 h-4 mr-2" />
                            Salary Config
                        </TabsTrigger>
                    )}
                </TabsList>

                {/* ADMIN: Payroll Processing */}
                {canManagePayroll && (
                    <TabsContent value="management" className="space-y-4">
                        <Card>
                            <CardHeader>
                                <CardTitle>Process Monthly Payroll</CardTitle>
                                <CardDescription>Generate payslips for all active employees</CardDescription>
                            </CardHeader>
                            <CardContent>
                                <div className="flex flex-col sm:flex-row gap-4 items-end bg-secondary/20 p-4 rounded-lg">
                                    <div className="space-y-2">
                                        <Label>Month</Label>
                                        <Select value={selectedMonth.toString()} onValueChange={(v) => setSelectedMonth(parseInt(v))}>
                                            <SelectTrigger className="w-40">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {MONTHS.map(m => <SelectItem key={m.value} value={m.value.toString()}>{m.label}</SelectItem>)}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Year</Label>
                                        <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
                                            <SelectTrigger className="w-32">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {YEARS.map(y => <SelectItem key={y} value={y.toString()}>{y}</SelectItem>)}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <Button
                                        className="glow-primary"
                                        onClick={handleRunPayroll}
                                        disabled={payrollLoading}
                                    >
                                        {payrollLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Calculator className="w-4 h-4 mr-2" />}
                                        Run Payroll
                                    </Button>
                                </div>

                                <div className="mt-6 border rounded-lg overflow-hidden">
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead>Period</TableHead>
                                                <TableHead>Payslips</TableHead>
                                                <TableHead>Status</TableHead>
                                                <TableHead className="text-right">Actions</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {payrollRuns.length === 0 ? (
                                                <TableRow>
                                                    <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                                                        <History className="w-12 h-12 mx-auto mb-2 opacity-50" />
                                                        No payroll runs yet
                                                    </TableCell>
                                                </TableRow>
                                            ) : (
                                                payrollRuns.map((run) => (
                                                    <TableRow key={run.id}>
                                                        <TableCell className="font-medium">
                                                            {MONTHS.find(m => m.value === run.month)?.label} {run.year}
                                                        </TableCell>
                                                        <TableCell>{run.payslips_count ?? '—'}</TableCell>
                                                        <TableCell>
                                                            <Badge variant={run.status === 'finalized' ? 'default' : 'secondary'}>
                                                                {run.status}
                                                            </Badge>
                                                        </TableCell>
                                                        <TableCell className="text-right">
                                                            {run.status !== 'finalized' && (
                                                                <Button
                                                                    size="sm"
                                                                    onClick={() => handleFinalizePayroll(run.id)}
                                                                    disabled={finalizeLoading === run.id}
                                                                >
                                                                    {finalizeLoading === run.id
                                                                        ? <Loader2 className="w-4 h-4 animate-spin" />
                                                                        : <CheckCircle className="w-4 h-4 mr-1" />}
                                                                    Finalize
                                                                </Button>
                                                            )}
                                                        </TableCell>
                                                    </TableRow>
                                                ))
                                            )}
                                        </TableBody>
                                    </Table>
                                </div>

                                {/* EPF Report Download */}
                                <div className="mt-6 p-4 border rounded-lg bg-secondary/10">
                                    <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                                        <Download className="w-4 h-4" /> EPF Report
                                    </h3>
                                    <div className="flex flex-wrap gap-3 items-end">
                                        <div className="space-y-1">
                                            <Label>Month</Label>
                                            <Select value={epfMonth.toString()} onValueChange={(v) => setEpfMonth(parseInt(v))}>
                                                <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
                                                <SelectContent>{MONTHS.map(m => <SelectItem key={m.value} value={m.value.toString()}>{m.label}</SelectItem>)}</SelectContent>
                                            </Select>
                                        </div>
                                        <div className="space-y-1">
                                            <Label>Year</Label>
                                            <Select value={epfYear.toString()} onValueChange={(v) => setEpfYear(parseInt(v))}>
                                                <SelectTrigger className="w-28"><SelectValue /></SelectTrigger>
                                                <SelectContent>{YEARS.map(y => <SelectItem key={y} value={y.toString()}>{y}</SelectItem>)}</SelectContent>
                                            </Select>
                                        </div>
                                        <Button variant="outline" onClick={handleDownloadEpf}>
                                            <Download className="w-4 h-4 mr-2" /> Download CSV
                                        </Button>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </TabsContent>
                )}

                {/* EMPLOYEE: My Payslips */}
                <TabsContent value="my-payslips">
                    <Card>
                        <CardHeader>
                            <CardTitle>My Payslips</CardTitle>
                            <CardDescription>Your historical earnings and deductions</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Period</TableHead>
                                        <TableHead>Gross Salary</TableHead>
                                        <TableHead>Deductions</TableHead>
                                        <TableHead>Net Pay</TableHead>
                                        <TableHead className="text-right">Action</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {myPayslips.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                                                No payslips found
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        myPayslips.map((slip) => (
                                            <TableRow key={slip.id}>
                                                <TableCell className="font-medium">
                                                    {MONTHS.find(m => m.value === slip.month)?.label} {slip.year}
                                                </TableCell>
                                                <TableCell>₹{slip.gross_salary.toLocaleString()}</TableCell>
                                                <TableCell className="text-red-500">₹{slip.total_deductions.toLocaleString()}</TableCell>
                                                <TableCell className="font-bold text-green-600">₹{slip.net_salary.toLocaleString()}</TableCell>
                                                <TableCell className="text-right">
                                                    <Button variant="ghost" size="sm" onClick={() => handleDownloadPayslip(slip.id, slip.month, slip.year)}>
                                                        <Download className="w-4 h-4 mr-2" />
                                                        PDF
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* ADMIN: Salary Config */}
                {canManagePayroll && (
                    <TabsContent value="settings">
                        <Card>
                            <CardHeader>
                                <CardTitle>Salary Structures</CardTitle>
                                <CardDescription>Configure compensation details for each employee</CardDescription>
                            </CardHeader>
                            <CardContent>
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Employee</TableHead>
                                            <TableHead>Department</TableHead>
                                            <TableHead>Designation</TableHead>
                                            <TableHead className="text-right">Action</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {employees.filter(e => e.is_active).map((emp) => (
                                            <TableRow key={emp.id}>
                                                <TableCell className="font-medium">{emp.first_name} {emp.last_name}</TableCell>
                                                <TableCell><Badge variant="outline">{emp.department}</Badge></TableCell>
                                                <TableCell>{emp.designation}</TableCell>
                                                <TableCell className="text-right">
                                                    <Button variant="outline" size="sm" onClick={() => handleOpenSalary(emp)}>
                                                        <Settings2 className="w-4 h-4 mr-2" />
                                                        Configure
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </CardContent>
                        </Card>
                    </TabsContent>
                )}
            </Tabs>

            {/* Salary Config Dialog */}
            <Dialog open={showSalaryDialog} onOpenChange={setShowSalaryDialog}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader>
                        <DialogTitle>Salary Configuration</DialogTitle>
                        <CardDescription>
                            Set earnings and deductions for {editingEmployee?.first_name} {editingEmployee?.last_name}
                        </CardDescription>
                    </DialogHeader>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 py-4">
                        <div className="space-y-4">
                            <h3 className="text-sm font-semibold border-b pb-1">Earnings</h3>
                            <div className="space-y-2">
                                <Label>Basic Salary (Monthly) *</Label>
                                <Input
                                    type="number"
                                    value={salaryForm.basic_salary}
                                    onChange={(e) => setSalaryForm({ ...salaryForm, basic_salary: parseFloat(e.target.value) || 0 })}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>HRA (Monthly) *</Label>
                                <Input
                                    type="number"
                                    value={salaryForm.hra}
                                    onChange={(e) => setSalaryForm({ ...salaryForm, hra: parseFloat(e.target.value) || 0 })}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Special Allowance</Label>
                                <Input
                                    type="number"
                                    value={salaryForm.special_allowance}
                                    onChange={(e) => setSalaryForm({ ...salaryForm, special_allowance: parseFloat(e.target.value) || 0 })}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Conveyance</Label>
                                <Input
                                    type="number"
                                    value={salaryForm.conveyance}
                                    onChange={(e) => setSalaryForm({ ...salaryForm, conveyance: parseFloat(e.target.value) || 0 })}
                                />
                            </div>
                        </div>

                        <div className="space-y-4">
                            <h3 className="text-sm font-semibold border-b pb-1">Deductions & Statutory</h3>
                            <div className="space-y-2">
                                <Label>Professional Tax</Label>
                                <Input
                                    type="number"
                                    value={salaryForm.professional_tax}
                                    onChange={(e) => setSalaryForm({ ...salaryForm, professional_tax: parseFloat(e.target.value) || 0 })}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>TDS / Income Tax</Label>
                                <Input
                                    type="number"
                                    value={salaryForm.tds}
                                    onChange={(e) => setSalaryForm({ ...salaryForm, tds: parseFloat(e.target.value) || 0 })}
                                />
                            </div>
                            <div className="flex items-center space-x-2 pt-4">
                                <input
                                    type="checkbox"
                                    id="epf_app"
                                    checked={salaryForm.epf_applicable}
                                    onChange={(e) => setSalaryForm({ ...salaryForm, epf_applicable: e.target.checked })}
                                    className="w-4 h-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
                                />
                                <Label htmlFor="epf_app">EPF Applicable (12%)</Label>
                            </div>
                            <div className="space-y-2 pt-2">
                                <Label>Effective From Date</Label>
                                <Input
                                    type="date"
                                    value={salaryForm.effective_from}
                                    onChange={(e) => setSalaryForm({ ...salaryForm, effective_from: e.target.value })}
                                />
                            </div>
                        </div>

                        <div className="md:col-span-2 bg-green-50 p-4 rounded-lg flex justify-between items-center border border-green-100">
                            <div className="text-sm text-green-800">
                                <p className="font-semibold text-lg">Estimated Gross: ₹{(salaryForm.basic_salary + salaryForm.hra + salaryForm.special_allowance + salaryForm.conveyance + salaryForm.other_allowances).toLocaleString()}</p>
                                <p className="text-xs">Based on monthly calculation logic</p>
                            </div>
                            <Button className="glow-primary" onClick={handleSaveSalary}>
                                Update Structure
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
